import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { CrmService } from '../../../services/crm.service';
import { Opportunity, OpportunityStage, LostReason } from '../../../classes/crm';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-customer-opportunities',
  templateUrl: './customer-opportunities.component.html',
  styleUrls: ['./customer-opportunities.component.scss']
})
export class CustomerOpportunitiesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() customerdata: any;
  @Output() opportunityUpdated = new EventEmitter<any>();

  opportunities: Opportunity[] = [];
  opportunityStages: OpportunityStage[] = [];
  lostReasons: LostReason[] = [];
  editingOpportunity: Opportunity | null = null;
  showCreateForm: boolean = false;
  loading: boolean = false;

  // Form data
  newOpportunity = {
    title: '',
    description: '',
    value: 0,
    expected_close_date: '',
    stage_id: 0
  };

  private destroy$ = new Subject<void>();

  constructor(
    private globalSearchService: GlobalSearchService,
    private crmService: CrmService
  ) { }

  ngOnInit(): void {
    this.loadOpportunityStages();
    this.loadLostReasons();
    if (this.customerdata) {
      this.loadOpportunities();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerdata'] && this.customerdata && !changes['customerdata'].firstChange) {
      this.loadOpportunities();
    }
  }

  loadOpportunityStages(): void {
    this.globalSearchService.opportunity_stages
      .pipe(takeUntil(this.destroy$))
      .subscribe(stages => {
        this.opportunityStages = stages || [];
      });
  }

  loadLostReasons(): void {
    this.globalSearchService.lost_reasons
      .pipe(takeUntil(this.destroy$))
      .subscribe(reasons => {
        this.lostReasons = reasons || [];
      });
  }

  loadOpportunities(): void {
    if (!this.customerdata?.debtorno) return;

    this.loading = true;
    this.crmService.getOpportunitiesByCustomer(
      this.customerdata.debtorno,
      this.customerdata.branchcode || 'MAIN'
    ).pipe(takeUntil(this.destroy$)).subscribe(
      (opportunities: Opportunity[]) => {
        this.opportunities = opportunities || [];
        this.loading = false;
      },
      error => {
        console.error('Error loading opportunities:', error);
        this.loading = false;
        alert('Failed to load opportunities. Please try again.');
      }
    );
  }

  createOpportunity(): void {
    if (!this.customerdata?.debtorno) return;

    const opportunityData = {
      debtorno: this.customerdata.debtorno,
      branchcode: this.customerdata.branchcode || 'MAIN',
      title: this.newOpportunity.title,
      description: this.newOpportunity.description,
      value: this.newOpportunity.value,
      expected_close_date: this.newOpportunity.expected_close_date,
      stage_id: this.newOpportunity.stage_id
    };

    this.crmService.createOpportunity(opportunityData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          this.loadOpportunities();
          this.resetForm();
          this.showCreateForm = false;
          this.opportunityUpdated.emit(result);
        },
        error => {
          console.error('Error creating opportunity:', error);
          alert('Failed to create opportunity. Please try again.');
        }
      );
  }

  updateOpportunityStage(opportunity: Opportunity, stageId: number): void {
    this.crmService.updateOpportunityStage(opportunity.id, stageId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loadOpportunities();
          this.opportunityUpdated.emit(opportunity);
        },
        error => console.error('Error updating opportunity stage:', error)
      );
  }

  markAsLost(opportunity: Opportunity, lostReasonId: number): void {
    const updateData = {
      stage_id: opportunity.stage_id,
      lost_reason_id: lostReasonId
    };

    this.crmService.updateOpportunity(opportunity.id, updateData)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.loadOpportunities();
          this.opportunityUpdated.emit(opportunity);
        },
        error => console.error('Error marking opportunity as lost:', error)
      );
  }

  deleteOpportunity(opportunity: Opportunity): void {
    if (confirm('Are you sure you want to delete this opportunity?')) {
      this.crmService.deleteOpportunity(opportunity.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.loadOpportunities();
            this.opportunityUpdated.emit(opportunity);
          },
          error => console.error('Error deleting opportunity:', error)
        );
    }
  }

  getStageColor(stageId: number): string {
    const stage = this.opportunityStages.find(s => s.id === stageId);
    return stage?.color || '#6c757d';
  }

  getStageName(stageId: number): string {
    const stage = this.opportunityStages.find(s => s.id === stageId);
    return stage?.name || 'Unknown';
  }

  getTotalValue(): number {
    return this.opportunities.reduce((total, opp) => total + (opp.value || 0), 0);
  }

  getOpenValue(): number {
    return this.opportunities
      .filter(opp => !opp.stage_closed)
      .reduce((total, opp) => total + (opp.value || 0), 0);
  }

  getWonValue(): number {
    return this.opportunities
      .filter(opp => opp.stage_closed && opp.stage_name?.toLowerCase().includes('won'))
      .reduce((total, opp) => total + (opp.value || 0), 0);
  }

  resetForm(): void {
    this.newOpportunity = {
      title: '',
      description: '',
      value: 0,
      expected_close_date: '',
      stage_id: 0
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
